/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { Reducer } from 'redux';
import { APIActions } from '../actions/apiActions';
import DataState from '../enums/DataState';
import MetadataState from '../enums/MetadataState';
import { IState } from '../store/types/StateTypes';
import { IMetadataReducerActionData, IReducerAction } from '../store/types/ReducerTypes';
import { resetMultiAxisDataSet } from './multiAxisDataReducer';
import { resetMultiLevelDataset } from './multiLevelDataReducer';
import { updateFilterParameters } from './metadataReducerHelpers/filterHelper';
import { resetDynamicProperties } from '../store/visualizationTypesRegistry';
import showTitleReducer from './titleOptionsHelpers/showTitleReducer';

function paramsSame(params1: any = {}, params2: any = {}) {
    const keys1 = Object.keys(params1).sort();
    const keys2 = Object.keys(params2).sort();

    let result = keys1.reduce((memo, key1, index) => (memo && (key1 === keys2[index])), keys1.length === keys2.length);

    for (let i = 0; result && i < keys2.length; i += 1) {
        const sortedParams1 = params1[keys1[i]].sort() as string[];
        const sortedParams2 = params2[keys2[i]].sort() as string[];

        result = sortedParams1.reduce(
            (memo, param1, index) => (memo && (param1 === sortedParams2[index])),
            sortedParams1.length === sortedParams2.length
        );
    }

    return result;
}

const apiReducer: Reducer<any, IReducerAction<IMetadataReducerActionData>> = (state: IState, action: IReducerAction<IMetadataReducerActionData>) => {
    let newState = state;
    let datasetResetRequired = false;

    if (action.type === APIActions.API_REFRESH) {
        const actionData = action.data;
        if (!actionData) {
            throw new Error('Action data wasn\'t put');
        }

        newState = {
            ...newState,
            deferredAction: actionData.deferredAction || newState.deferredAction,
            metadataState: MetadataState.LOADING,
            metadataError: null,
            dataState: DataState.LOADING,
            dataError: null,
            refreshData: true
        };
    }

    if (action.type === APIActions.API_DESTROY) {
        const actionData = action.data;
        if (!actionData) {
            throw new Error('Action data wasn\'t put');
        }

        newState = {
            ...newState,
            deferredAction: actionData.deferredAction || newState.deferredAction,
            metadataState: MetadataState.DESTROYED,
            metadataError: null,
            dataState: DataState.DESTROYED,
            dataError: null,
            refreshData: false
        };
    }

    if (action.type === APIActions.API_RESIZE) {
        const actionData = action.data;
        if (!actionData) {
            throw new Error('Action data wasn\'t put');
        }

        const { width, height } = actionData.properties;

        newState = {
            ...state,
            adHocViewHeight: height,
            adHocViewWidth: width,
            refreshData: false
        };
    }

    if (action.type === APIActions.API_PROPERTIES_UPDATE) {
        const actionData = action.data;

        if (!actionData) {
            throw new Error('Action data wasn\'t put');
        }

        newState.deferredAction = actionData.deferredAction || newState.deferredAction;

        if (newState.resourceURI !== actionData.properties.resource || newState.server !== actionData.properties.server) {
            newState = {
                ...newState,
                server: actionData.properties.server,
                resourceURI: actionData.properties.resource,
                metadataState: MetadataState.LOADING,
                metadataError: null,
                dataState: MetadataState.LOADING,
                dataError: null,
                refreshData: false
            };
        }

        if (actionData.properties.canvas) {
            newState = {
                ...newState,
                canvas: actionData.properties.canvas,
                refreshData: false
            };

            if (actionData.properties.canvas.type) {
                const selectedVisualizationType = state.visualizationTypesRegistry.find((item: any): boolean => {
                    return item.descriptor.name === actionData.properties.canvas.type;
                });

                if (selectedVisualizationType) {
                    newState = {
                        ...newState,
                        visualizationDescriptor: selectedVisualizationType.descriptor,
                    };
                }
            }
        }

        if (actionData.properties.linkOptions) {
            newState = {
                ...newState,
                linkOptions: actionData.properties.linkOptions,
                refreshData: false
            };
        }

        if (actionData.properties.visualizationChooser) {
            newState = {
                ...newState,
                visualizationChooser: {
                    ...newState.visualizationChooser,
                    ...actionData.properties.visualizationChooser
                },
                refreshData: false
            };
        }

        if (typeof actionData.properties.showTitle !== 'undefined') {
            // here we grab the API props from the user
            newState = {
                ...newState,
                titleOptions: {
                    ...newState.titleOptions,
                    showTitleFlagFromAPI: actionData.properties.showTitle
                },
                refreshData: false
            };
        }

        if (typeof actionData.properties.designerMode !== 'undefined') {
            newState = {
                ...newState,
                isInDesignerMode: !!actionData.properties.designerMode,
                refreshData: false
            };
        }

        newState.titleOptions.showTitle = showTitleReducer(newState);

        if (actionData.properties.params && !paramsSame(state.params, actionData.properties.params)) {
            newState = {
                ...newState,
                params: actionData.properties.params,
                refreshData: false
            };

            if (newState.metadataState !== MetadataState.LOADING) {
                newState.filterParameters = updateFilterParameters(state.filterParameters, actionData.properties.params);
                datasetResetRequired = true;
            }
        }

        if (typeof actionData.properties.events === 'object') {
            newState = {
                ...newState,
                eventCallbacks: actionData.properties.events,
                refreshData: false
            };
        }

        // if there were previously set invalid parameters, invalid state should be reevaluated with newly set parameters
        if (newState !== state && newState.metadataState === MetadataState.INVALID) {
            newState.metadataState = MetadataState.LOADED;
            newState.metadataError = null;
        }
    }

    if (datasetResetRequired) {
        newState = resetMultiAxisDataSet(newState, state.metadataObj);
        newState = resetMultiLevelDataset(newState);
        newState = resetDynamicProperties(newState);
    }

    return newState;
};

export default apiReducer;
